/**
 * src/data/site-settings.ts
 *
 * Site-wide settings — header menu, footer columns, contact, social, copyright.
 * Phase α'da TS literal (bu dosya), Phase β'da Drizzle `site_settings` tablosunda
 * JSONB olarak saklanir. Schema = src/data/types/site-settings.ts.
 *
 * Veri kaynagi: data/live-snapshots/nav-tree.json (canli securesys.com.tr'den
 * 2026-05-25'te cikarildi).
 *
 * SRP-istisna notu: code-style.md 60 satir soft limit asilmis. Bu dosya **sadece
 * veri** (logic yok), tek concern: 'site genelinde paylasilan sabit yapilandirma'.
 * Phase β'da DB JSONB'sine tasinacak, kullanim donemi belirsiz uzun degil.
 */

import type { LocalizedString } from '@/domain/value-objects/localized-string'
import type { SiteSettings } from './types/site-settings'

/**
 * Locale-aware string olusturmak icin shorthand.
 * `tr('Merhaba', 'Hello')` -> `{ tr: 'Merhaba', en: 'Hello' }`
 */
const ls = (tr: string, en: string): LocalizedString => ({ tr, en })

export const siteSettings: SiteSettings = {
  siteName: 'SecureSys',
  tagline: ls('Kurumsal Siber Güvenlik Çözümleri', 'Enterprise Cyber Security Solutions'),

  /*
   * Locale registry — 15 yaygın dil bootstrap fixture'ı. Bu **initial defaults**;
   * gerçek aktif liste DB'de (site_settings.locales JSONB). Admin /cms/diller'dan
   * enabled toggle + label edit + default değişimi yapar.
   *
   * Neden 15'i önceden listeli? Middleware ve next-intl routing build-time const
   * okuyor (edge runtime sync gerek). Locale CODE listesi build-time'da bilinmek
   * zorunda; admin sadece enabled flag + label + routeSegments yönetir. Yeni bir
   * locale CODE (mesela 'sw' Swahili) eklemek istenirse bu listeye satır eklenip
   * deploy gerekir — pratikte 15 dil %99 use case'i karşılar.
   *
   * Sıralama: ISO 639-1 alfabetik, TR ilk (varsayılan).
   * RTL: ar, he (sağdan sola).
   */
  locales: [
    {
      code: 'tr',
      nativeLabel: 'Türkçe',
      englishLabel: 'Turkish',
      dir: 'ltr',
      isDefault: true,
      enabled: true,
    },
    {
      code: 'en',
      nativeLabel: 'English',
      englishLabel: 'English',
      dir: 'ltr',
      isDefault: false,
      enabled: true,
    },
    {
      code: 'ar',
      nativeLabel: 'العربية',
      englishLabel: 'Arabic',
      dir: 'rtl',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'de',
      nativeLabel: 'Deutsch',
      englishLabel: 'German',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'es',
      nativeLabel: 'Español',
      englishLabel: 'Spanish',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'fr',
      nativeLabel: 'Français',
      englishLabel: 'French',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'he',
      nativeLabel: 'עברית',
      englishLabel: 'Hebrew',
      dir: 'rtl',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'hi',
      nativeLabel: 'हिन्दी',
      englishLabel: 'Hindi',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'it',
      nativeLabel: 'Italiano',
      englishLabel: 'Italian',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'ja',
      nativeLabel: '日本語',
      englishLabel: 'Japanese',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'ko',
      nativeLabel: '한국어',
      englishLabel: 'Korean',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'nl',
      nativeLabel: 'Nederlands',
      englishLabel: 'Dutch',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'pl',
      nativeLabel: 'Polski',
      englishLabel: 'Polish',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'pt',
      nativeLabel: 'Português',
      englishLabel: 'Portuguese',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'ru',
      nativeLabel: 'Русский',
      englishLabel: 'Russian',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
    {
      code: 'zh',
      nativeLabel: '中文',
      englishLabel: 'Chinese',
      dir: 'ltr',
      isDefault: false,
      enabled: false,
    },
  ],

  /*
   * Route segment mappings — listing path'leri per-locale. navigation.tsx bu listeden
   * runtime'da next-intl pathnames objesini build eder.
   *
   * Yeni statik sayfa eklemek: pattern: 'static' + segments map.
   * Yeni listing + detay eklemek: pattern: 'dynamic-slug' + segments map (listing path'i).
   */
  routeSegments: [
    // Static sayfa EN slug'ları DB sayfalar.slug.en ile birebir AYNI olmalı —
    // catch-all [sayfa] handler DB lookup yapar, locale switcher next-intl
    // pathnames'i (bu liste) kullanır. Tutarsızlık → locale switch'te 404.
    { key: 'home', pattern: 'static', segments: { tr: '/', en: '/' } },
    { key: 'blog', pattern: 'dynamic-slug', segments: { tr: '/blog', en: '/blog' } },
    { key: 'etiket', pattern: 'dynamic-slug', segments: { tr: '/etiket', en: '/tag' } },
    { key: 'kategori', pattern: 'dynamic-slug', segments: { tr: '/kategori', en: '/category' } },
    { key: 'yazar', pattern: 'dynamic-slug', segments: { tr: '/yazar', en: '/author' } },
    { key: 'hizmetler', pattern: 'dynamic-slug', segments: { tr: '/hizmetler', en: '/services' } },
    // Bilgi Merkezi — konu-bagimsiz egitici icerik hub'i (Cloudflare/Imperva
    // "Learning Center" deseni). hizmetler tablosunda yasar (FeaturedHTMLBlock +
    // CTABanner altyapisi yeniden kullanilir) ama ayri route + ayri taxonomy
    // (bilgi_merkezi_kategori) ile /hizmetler katalogundan izole.
    {
      key: 'bilgi-merkezi',
      pattern: 'dynamic-slug',
      segments: { tr: '/bilgi-merkezi', en: '/learning' },
    },
    // Ürünler — bayilik/lisans satışı yapılan 3. parti ürünler (Global/Yerli
    // Ürünler + Teknoloji İş Ortaklarımız). hizmetler tablosunda yaşar ama
    // urun_kategori taxonomy'si ile /hizmetler katalogundan izole (2026-07-24:
    // önceden /hizmetler/<slug> altında yanlışlıkla hizmet gibi görünüyordu).
    { key: 'urunler', pattern: 'dynamic-slug', segments: { tr: '/urunler', en: '/products' } },
    { key: 'iletisim', pattern: 'static', segments: { tr: '/iletisim', en: '/contact' } },
    { key: 'hakkimizda', pattern: 'static', segments: { tr: '/hakkimizda', en: '/about' } },
    {
      key: 'misyonumuz-ve-vizyonumuz',
      pattern: 'static',
      segments: { tr: '/misyonumuz-ve-vizyonumuz', en: '/mission-vision' },
    },
    {
      key: 'referanslarimiz',
      pattern: 'static',
      segments: { tr: '/referanslarimiz', en: '/references' },
    },
    // 2026-08-14: eski /urunler/sistem-urunleri-ve-network ve
    // /urunler/siber-guvenlik-urunleri sayfalarinin birlesimi; ikisi 308 ile buraya gider.
    {
      key: 'cozum-ortaklarimiz',
      pattern: 'static',
      segments: { tr: '/cozum-ortaklarimiz', en: '/solution-partners' },
    },
    {
      key: 'kalite-sertifikalarimiz',
      pattern: 'static',
      segments: { tr: '/kalite-sertifikalarimiz', en: '/certifications' },
    },
    {
      key: 'sikca-sorulan-sorular',
      pattern: 'static',
      segments: { tr: '/sikca-sorulan-sorular', en: '/faq' },
    },
    {
      key: 'organizasyon-semasi',
      pattern: 'static',
      segments: { tr: '/organizasyon-semasi', en: '/organization' },
    },
    {
      key: 'kariyer-merkezi',
      pattern: 'static',
      segments: { tr: '/kariyer-merkezi', en: '/careers' },
    },
    {
      key: 'musteri-yonetim-yaklasimimiz',
      pattern: 'static',
      segments: { tr: '/musteri-yonetim-yaklasimimiz', en: '/customer-approach' },
    },
    {
      key: 'kalite-yonetim-sistemi',
      pattern: 'static',
      segments: { tr: '/kalite-yonetim-sistemi', en: '/quality-management' },
    },
    {
      key: 'iso-27001-bgys-politikasi',
      pattern: 'static',
      segments: { tr: '/iso-27001-bgys-politikasi', en: '/iso-27001-policy' },
    },
    {
      key: 'birlesik-entegre-yonetim-sistemi-politikasi',
      pattern: 'static',
      segments: {
        tr: '/birlesik-entegre-yonetim-sistemi-politikasi',
        en: '/integrated-management-policy',
      },
    },
    {
      key: 'kvkk-aydinlatma',
      pattern: 'static',
      segments: { tr: '/kvkk-aydinlatma', en: '/privacy-notice' },
    },
    {
      key: 'gizlilik-politikasi',
      pattern: 'static',
      segments: { tr: '/gizlilik-politikasi', en: '/privacy-policy' },
    },
    {
      key: 'cerez-politikasi',
      pattern: 'static',
      segments: { tr: '/cerez-politikasi', en: '/cookie-policy' },
    },
  ],

  /*
   * RBAC roller — Phase β'da Lucia auth bu listeye gore yetki kontrolu yapar.
   * Phase α'da admin yok; ancak yapiyi simdiden kuruyoruz ki migration sirasinda
   * sema sabit kalsin. system: true olan roller silinemez/code degistirilemez.
   */
  roles: [
    {
      code: 'admin',
      label: ls('Yönetici', 'Administrator'),
      permissions: ['*'],
      system: true,
    },
    {
      code: 'editor',
      label: ls('İçerik Editörü', 'Content Editor'),
      permissions: ['content:read', 'content:write', 'media:read', 'media:write'],
      system: true,
    },
    {
      code: 'viewer',
      label: ls('Görüntüleyici', 'Viewer'),
      permissions: ['content:read', 'media:read'],
      system: true,
    },
  ],

  /*
   * Logo naming:
   *   logoLight = light theme'de gosterilecek logo (koyu yazili, beyaz bg uzerinde okunur)
   *   logoDark  = dark theme'de gosterilecek logo (acik/beyaz yazili, koyu bg uzerinde okunur)
   * Dosya isimlendirmesi tersi: 'logo-dark.webp' icindeki yazinin rengi koyu (light bg icin).
   */
  brand: {
    logoLight: '/brand/securesys-logo-dark.webp',
    logoDark: '/brand/securesys-logo-white.webp',
    favicon: '/brand/securesys-logo-dark.webp',
  },

  topbar: {
    show: true,
    cta: {
      label: ls('Bize Ulaşın', 'Contact Us'),
      href: '/iletisim',
    },
  },

  /*
   * Main nav — canli site'deki mega menu birebir. 6 kategori, ~110 link.
   * Slug'lar locale-aware degil simdilik (TR slug both locale'larda); EN klon
   * implementasyonu Phase α Week 6'da DeepL ile sayfa basliklarini ve URL'leri
   * cevirip burada update edilecek.
   */
  mainMenu: [
    {
      label: ls('Kurumsal', 'Corporate'),
      href: null,
      children: [
        { label: ls('Hakkımızda', 'About Us'), href: '/hakkimizda' },
        {
          label: ls('Misyonumuz ve Vizyonumuz', 'Our Mission and Vision'),
          href: '/misyonumuz-ve-vizyonumuz',
        },
        { label: ls('Organizasyon Şeması', 'Organization Chart'), href: '/organizasyon-semasi' },
        {
          label: ls('Kalite Sertifikalarımız', 'Quality Certifications'),
          href: '/kalite-sertifikalarimiz',
        },
        { label: ls('Referanslarımız', 'References'), href: '/referanslarimiz' },
        {
          label: ls(
            'Birleşik-Entegre Yönetim Sistemi Politikası',
            'Integrated Management System Policy',
          ),
          href: '/birlesik-entegre-yonetim-sistemi-politikasi',
        },
        {
          label: ls('Kalite Yönetim Sistemi', 'Quality Management System'),
          href: '/kalite-yonetim-sistemi',
        },
        {
          label: ls('Müşteri Yönetim Yaklaşımımız', 'Customer Management Approach'),
          href: '/musteri-yonetim-yaklasimimiz',
        },
        {
          label: ls('ISO 27001 BGYS Politikası', 'ISO 27001 ISMS Policy'),
          href: '/iso-27001-bgys-politikasi',
        },
        { label: ls('Gizlilik Politikası', 'Privacy Policy'), href: '/gizlilik-politikasi' },
        { label: ls('KVKK Aydınlatma Metni', 'Privacy Notice (KVKK)'), href: '/kvkk-aydinlatma' },
        { label: ls('Çerez Politikası', 'Cookie Policy'), href: '/cerez-politikasi' },
        { label: ls('Kariyer Merkezi', 'Career Center'), href: '/kariyer-merkezi' },
        { label: ls('Sıkça Sorulan Sorular', 'FAQ'), href: '/sikca-sorulan-sorular' },
      ],
    },
    {
      label: ls('Siber Güvenlik', 'Cyber Security'),
      href: null,
      children: [
        {
          label: ls('Sızma Testi Hizmeti', 'Penetration Test Service'),
          href: '/hizmetler/sizma-testi-penetrasyon-testi-hizmeti',
        },
        {
          label: ls('Red Teaming Hizmeti', 'Red Teaming Service'),
          href: '/hizmetler/red-teaming-hizmeti',
        },
        {
          label: ls('Kaynak Kod Analizi', 'Source Code Analysis'),
          href: '/hizmetler/kaynak-kod-analizi',
        },
        {
          label: ls('Yük Testi Hizmeti', 'Load Testing Service'),
          href: '/hizmetler/yuk-testi-hizmeti',
        },
        {
          label: ls('Siber İstihbarat', 'Cyber Intelligence'),
          href: '/hizmetler/siber-istihbarat',
        },
        {
          label: ls('Adli Bilişim Hizmeti', 'Digital Forensics'),
          href: '/hizmetler/adli-bilisim-hizmeti',
        },
        {
          label: ls('Veritabanı Güvenlik Analizi', 'Database Security Analysis'),
          href: '/hizmetler/veritabani-guvenlik-analizi-hizmeti',
        },
        {
          label: ls('Stres Testi Hizmeti', 'Stress Testing'),
          href: '/hizmetler/stres-testi-hizmeti',
        },
        {
          label: ls('İtibar Koruma DMCA Takedown', 'Reputation Protection & DMCA Takedown'),
          href: '/hizmetler/itibar-koruma-dmca-takedown-hizmeti',
        },
      ],
    },
    {
      // Sızma Testi — penetrasyon-testi-turleri sayfasındaki "Test Türleri"
      // listesinin 10 alt sayfası (her biri ayrı hizmet, DB'de published).
      label: ls('Sızma Testi', 'Penetration Testing'),
      href: null,
      children: [
        {
          label: ls(
            'Kablosuz Ağ Güvenlik Test Hizmeti',
            'Wireless Network Security Testing Service',
          ),
          href: '/hizmetler/kablosuz-ag-guvenlik-test-hizmeti',
        },
        {
          label: ls('Yerel Ağ Güvenlik Test Hizmeti', 'Local Network Security Testing Service'),
          href: '/hizmetler/yerel-ag-guvenlik-test-hizmeti',
        },
        {
          label: ls(
            'Web Uygulama Güvenlik Test Hizmeti',
            'Web Application Security Testing Service',
          ),
          href: '/hizmetler/web-uygulama-guvenlik-test-hizmeti',
        },
        {
          label: ls(
            'Mobil Uygulama Güvenlik Test Hizmeti',
            'Mobile Application Security Testing Service',
          ),
          href: '/hizmetler/mobil-uygulama-guvenlik-test-hizmeti',
        },
        {
          label: ls(
            'Web Servisi/API Güvenlik Test Hizmeti',
            'Web Service/API Security Testing Service',
          ),
          href: '/hizmetler/web-servisi-api-guvenlik-test-hizmeti',
        },
        {
          label: ls('Sosyal Mühendislik Test Hizmeti', 'Social Engineering Testing Service'),
          href: '/hizmetler/sosyal-muhendislik-test-hizmeti',
        },
        {
          label: ls('DDoS Test Hizmeti', 'DDoS Test Service'),
          href: '/hizmetler/ddos-test-hizmeti',
        },
        {
          label: ls('VoIP Güvenlik Test Hizmeti', 'VoIP Security Testing Service'),
          href: '/hizmetler/voip-guvenlik-test-hizmeti',
        },
        {
          label: ls('Zararlı Yazılım Analiz Testi Hizmeti', 'Malware Analysis Test Service'),
          href: '/hizmetler/zararli-yazilim-analiz-testi-hizmeti',
        },
        {
          label: ls('SCADA EKS Sızma Testi Hizmeti', 'SCADA EKS Penetration Test Service'),
          href: '/hizmetler/scada-eks-sizma-testi-hizmeti',
        },
      ],
    },
    {
      // SİTE HARİTASI.xlsx dosyasındaki "BT Hizmetlerimiz & Bulut" sütununun
      // BİREBİR karşılığı — 13 satır, düz liste, alt grup yok. Sıra Excel'in
      // kendi sırası; kolonun içeriği zip'in numaralı klasörlerinden okunur
      // (site-icerik/README.md §51'de doğrulanmış kural: baştaki numara = Excel
      // satır sırası).
      //
      // 🔴 2026-08-14: burası eskiden iki ayrı başlıktı ("BT Hizmetlerimiz" 9
      // grup + "Bulut & Altyapı" 4 grup) ve toplam 69 link taşıyordu. Excel'e
      // indirgenince 50 sayfa MENÜDEN ÇIKTI — silinmediler, yayında ve
      // sitemap'teler, yalnız menüden erişilemiyorlar. Çıkarılan ağaç
      // `.tmp/menu-bt-cikarilanlar-*.json` içinde; çoğu Excel'in DİĞER
      // kolonlarına ait (Güvenlik Kamera, Kartlı Geçiş, Yapısal Kablolama,
      // Regülasyon Danışmanlık…) ve o partiler geldiğinde oraya yerleşecek.
      //
      // NOT: bu blok yalnızca DB'deki site_settings okunamazsa devreye giren
      // YEDEK menüdür — canlı menü DB'den gelir. Yine de doğru tutulmalı:
      // yanlış bir yedek, arıza anında eski/kırık menüyü canlıya çıkarır.
      label: ls('BT Hizmetlerimiz & Bulut', 'IT Services & Cloud'),
      href: null,
      children: [
        {
          label: ls('Firewall Kurulum Bakım', 'Firewall Installation & Maintenance'),
          href: '/hizmetler/firewall-kurulum-bakim-destek-hizmeti',
        },
        {
          label: ls('Network Bakım & VLAN', 'Network Maintenance & VLAN'),
          href: '/hizmetler/network-yonetimi-ag-bakim-vlan-hizmetleri',
        },
        {
          label: ls('Sistem & AD Kurulum Bakım', 'System & AD Installation & Maintenance'),
          href: '/hizmetler/sistem-yonetimi-active-directory-kurulum-bakim',
        },
        // 4- Database Veritabanı bakım → sayfası yok (Ramazan'a soruluyor), atlandı
        {
          label: ls('Switch Kurulum Bakım', 'Switch Installation & Maintenance'),
          href: '/hizmetler/switch-kurulum-ve-bakim-hizmeti',
        },
        {
          label: ls('Altyapı Kurulum Hizmetleri', 'Infrastructure Deployment Services'),
          href: '/hizmetler/bt-altyapi-kurulum-sistem-entegrasyon-hizmetleri',
        },
        {
          label: ls('SOC 7X24 İzleme Servisi', 'SOC 7X24 Monitoring Service'),
          href: '/hizmetler/soc-7x24-izleme-managed-soc-hizmeti',
        },
        {
          label: ls('Bulut Sunucu İşletme', 'Cloud Server Operations'),
          href: '/hizmetler/bulut-sunucu-isletme-yonetim-hizmeti',
        },
        {
          label: ls('Container As A Service (CaaS)', 'Container As A Service (CaaS)'),
          href: '/hizmetler/container-as-a-service-caas-kubernetes-hizmeti',
        },
        {
          label: ls('Database As A Service (DBaaS)', 'Database As A Service (DBaaS)'),
          href: '/hizmetler/database-as-a-service-dbaas',
        },
        {
          label: ls('Cold Backup & Yedekleme', 'Cold Backup & Backup'),
          href: '/hizmetler/cold-backup-yedekleme-immutable-backup-hizmeti',
        },
        {
          label: ls('Felaket Kurtarma Merkezi', 'Disaster Recovery Center'),
          href: '/hizmetler/felaket-kurtarma-merkezi-disaster-recovery-draas',
        },
        {
          label: ls('Kırmızı İzole Ağ Altyapı Kurulumu', 'Red / Isolated Network Deployment'),
          href: '/hizmetler/izole-ag-kirmizi-ag-yesil-ag-kurulumu',
        },
      ],
    },
    {
      // 2026-08-14: eski "Yazılım" başlığının yerini aldı. Eski başlıkta 3
      // maddenin adresi slug değişimiyle eskimişti, 1'inin adresi hiç yoktu;
      // altındaki 6 dil sayfası (Java/Python/.NET/PHP/JS/Frontend) emekliye
      // ayrılıp tek kapsamlı sayfada birleşti. İçerik Ramazan'ın
      // SİTE HARİTASI.xlsx dosyasındaki "Arge & Yazılım & AI" sütunudur;
      // Platform Engineering ve Software Supply Chain, Excel'de olmadığı hâlde
      // sayfaları hazır olduğu için konu kardeşleri DevOps'un yanına eklendi.
      //
      // NOT: bu blok yalnızca DB'deki site_settings okunamazsa devreye giren
      // YEDEK menüdür — canlı menü DB'den gelir. Yine de doğru tutulmalı:
      // yanlış bir yedek, arıza anında eski/kırık menüyü canlıya çıkarır.
      label: ls('Ar-Ge & Yazılım & AI', 'R&D, Software & AI'),
      href: null,
      children: [
        {
          label: ls('DevOps & DevSecOps', 'DevOps & DevSecOps'),
          href: '/hizmetler/devops-devsecops-hizmetleri',
        },
        {
          label: ls('Platform Engineering & IDP', 'Platform Engineering & IDP'),
          href: '/hizmetler/platform-engineering-internal-developer-platform-idp',
        },
        {
          label: ls('Software Supply Chain & SBOM', 'Software Supply Chain & SBOM'),
          href: '/hizmetler/software-supply-chain-security-sbom',
        },
        {
          label: ls('Veritabanı Servis Hizmeti', 'Database Service'),
          href: '/hizmetler/veritabani-servis-dba-yonetim-hizmetleri',
        },
        {
          label: ls('Medya Arşiv Depolama', 'Media Archiving & Storage'),
          href: '/hizmetler/dijital-icerik-arsivleme-depolama',
        },
        {
          label: ls('Yazılım Projelendirme & Ar-Ge', 'Software Project Design & R&D'),
          href: '/hizmetler/ozel-yazilim-gelistirme-arge-projelendirme',
        },
        {
          label: ls('Kaynak Kod Analizi & SAST', 'Source Code Analysis & SAST'),
          href: '/hizmetler/kaynak-kod-analizi-sast-hizmeti',
        },
        {
          label: ls('Mobil Yazılım Geliştirme', 'Mobile Application Development'),
          href: '/hizmetler/mobil-uygulama-gelistirme-hizmetleri',
        },
        {
          label: ls('LLM / Generative AI', 'LLM / Generative AI'),
          href: '/hizmetler/llm-generative-ai-cozumleri',
        },
        {
          label: ls('AI Agent / Agentic AI', 'AI Agent / Agentic AI'),
          href: '/hizmetler/ai-agent-agentic-ai-cozumleri',
        },
        {
          label: ls('RAG & Kurumsal Bilgi Asistanları', 'RAG & Knowledge Assistants'),
          href: '/hizmetler/rag-kurumsal-bilgi-asistani',
        },
        {
          label: ls('PostgreSQL Kurulum & Bakım', 'PostgreSQL Installation & Maintenance'),
          href: '/hizmetler/postgresql-kurulum-bakim-destek-hizmeti',
        },
      ],
    },
    {
      // Eski "Yazılım" başlığının altındaki hazır-yazılım grubu; konu olarak
      // Ar-Ge başlığına girmediği için ayrı başlık olarak korundu.
      label: ls('Hazır Yazılım & Entegrasyon', 'Off-the-Shelf Software & Integration'),
      href: null,
      children: [
        {
          label: ls('27001 BGYS & DDO Yazılımı', '27001 ISMS & PDP Software'),
          href: '/hizmetler/27001-bilgi-guvenligi-yonetim-ve-ddo-yazilimi',
        },
        {
          label: ls('KVKK Yönetimi Yazılımı', 'GDPR Management Software'),
          href: '/hizmetler/kvkk-yonetimi-yazilimi',
        },
        {
          label: ls('Verimlilik & Kullanıcı Takibi', 'Productivity & User Tracking'),
          href: '/hizmetler/verimlilik-ve-kullanici-guvenlik-takibi-yazilimi',
        },
      ],
    },
    {
      // SITE HARITASI.xlsx'teki "ÇÖZÜM ve ÜRÜNLER" sütununun BİREBİR
      // karşılığı — 16 satır, düz liste, alt grup yok. Başlık menüde diğer
      // yedi başlıkla aynı yazımda: "Çözüm ve Ürünler" (Excel'de büyük harf).
      //
      // 🔴 2026-08-15: burası eskiden "Ürünler" başlığıydı; altında üç alt
      // grupta (Teknoloji İş Ortaklarımız / Global / Yerli) 34 ürün sayfası
      // vardı. Excel sütununa indirgenince 33 sayfa MENÜDEN ÇIKTI —
      // silinmediler, yayında ve sitemap'teler, yalnız menüden erişilemiyorlar.
      // Çıkarılan ağaç .tmp/menu-coz-cikarilanlar-*.json içinde arşivli.
      //
      // Adresler ölçülerek yazıldı: 15 sayfanın 14'ü hizmet_kategori
      // (/hizmetler), yalnız apptracker urun_kategori (/urunler).
      label: ls('Çözüm ve Ürünler', 'Solutions & Products'),
      href: null,
      children: [
        { label: ls('Ürünler Kataloğu', 'Product Catalogue'), href: '/cozum-ortaklarimiz' },
        { label: ls('AI / LLM Sızma Testi', 'AI / LLM Penetration Testing'), href: '/hizmetler/ai-llm-sizma-testi-hizmeti' },
        { label: ls('Kalkan Siber Güvenlik Platformu', 'Kalkan Cyber Security Platform'), href: '/hizmetler/kalkan-siber-guvenlik-platformu' },
        { label: ls('Firewall Çözümleri', 'Firewall Solutions'), href: '/hizmetler/firewall-cozumleri-ngfw' },
        { label: ls('DLP & DDR Çözümleri', 'DLP & DDR Solutions'), href: '/hizmetler/dlp-ddr-cozumleri' },
        { label: ls('EDR & AV Çözümleri', 'EDR & AV Solutions'), href: '/hizmetler/edr-av-cozumleri' },
        { label: ls('DAM Çözümleri', 'DAM Solutions'), href: '/hizmetler/database-activity-monitoring-dam' },
        { label: ls('NAC Çözümleri', 'NAC Solutions'), href: '/hizmetler/nac-cozumleri' },
        { label: ls('AD & Microsoft Güvenlik Çözümleri', 'AD & Microsoft Security Solutions'), href: '/hizmetler/ad-microsoft-guvenlik-cozumleri' },
        { label: ls('PAM & MFA Çözümleri', 'PAM & MFA Solutions'), href: '/hizmetler/pam-mfa-cozumleri' },
        { label: ls('Mail & Sandbox Çözümleri', 'Mail & Sandbox Solutions'), href: '/hizmetler/mail-sandbox-guvenlik-cozumleri' },
        { label: ls('WAF Çözümleri', 'WAF Solutions'), href: '/hizmetler/waf-cozumleri-api-guvenligi' },
        { label: ls('Son Kullanıcı İzleme', 'End-User Monitoring'), href: '/urunler/son-kullanici-izleme-apptracker' },
        { label: ls('Veri Keşfi & Sınıflandırma', 'Data Discovery & Classification'), href: '/hizmetler/veri-kesfi-siniflandirma-cozumleri' },
        { label: ls('KVKK & BGYS Yazılımları', 'KVKK & ISMS Software'), href: '/hizmetler/kvkk-bgys-yazilimlari' },
        { label: ls('Siber Güvenlik Yazılımları', 'Cyber Security Software'), href: '/hizmetler/siber-guvenlik-yazilimlari' },
      ],
    },
    {
      label: ls('Blog', 'Blog'),
      href: '/blog',
    },
    {
      label: ls('Bilgi Merkezi', 'Learning Center'),
      href: '/bilgi-merkezi',
    },
  ],

  footerColumns: [
    {
      baslik: ls('Kurumsal', 'Corporate'),
      items: [
        { label: ls('Hakkımızda', 'About Us'), href: '/hakkimizda' },
        { label: ls('Organizasyon Şeması', 'Organization Chart'), href: '/organizasyon-semasi' },
        { label: ls('Referanslarımız', 'References'), href: '/referanslarimiz' },
        {
          label: ls('Kalite Sertifikalarımız', 'Certifications'),
          href: '/kalite-sertifikalarimiz',
        },
        { label: ls('Gizlilik Politikası', 'Privacy Policy'), href: '/gizlilik-politikasi' },
      ],
    },
    {
      baslik: ls('Siber Güvenlik', 'Cyber Security'),
      items: [
        {
          label: ls('Sızma Testi Hizmeti', 'Penetration Test'),
          href: '/hizmetler/sizma-testi-penetrasyon-testi-hizmeti',
        },
        { label: ls('Red Teaming', 'Red Teaming'), href: '/hizmetler/red-teaming-hizmeti' },
        {
          label: ls('Kaynak Kod Analizi', 'Source Code Analysis'),
          href: '/hizmetler/kaynak-kod-analizi',
        },
        {
          label: ls('Siber İstihbarat', 'Cyber Intelligence'),
          href: '/hizmetler/siber-istihbarat',
        },
        { label: ls('Adli Bilişim', 'Digital Forensics'), href: '/hizmetler/adli-bilisim-hizmeti' },
      ],
    },
    {
      baslik: ls('Network', 'Network'),
      items: [
        {
          label: ls('Log Korelasyon & İzleme', 'Log Correlation'),
          href: '/hizmetler/log-korelasyon-ve-izleme-cozumleri',
        },
        {
          label: ls('HotSpot Çözümü', 'HotSpot Solution'),
          href: '/hizmetler/hotspot-destegi-cozumu',
        },
        {
          label: ls('Switch Kurulum & Bakım', 'Switch Installation'),
          href: '/hizmetler/switch-kurulum-ve-bakim-hizmeti',
        },
        {
          label: ls('NAC Kurulum Destek', 'NAC Support'),
          href: '/hizmetler/nac-kurulum-bakim-destek-hizmeti',
        },
        {
          label: ls('IPS Kurulum Destek', 'IPS Support'),
          href: '/hizmetler/ips-kurulum-destek-hizmeti',
        },
      ],
    },
    {
      baslik: ls('Bulut & Yazılım', 'Cloud & Software'),
      items: [
        { label: ls('DevOps Hizmeti', 'DevOps Service'), href: '/hizmetler/devops-hizmeti' },
        {
          label: ls('Veritabanı Kurulum', 'Database Setup'),
          href: '/hizmetler/veritabani-kurulum-ve-bakim-hizmeti',
        },
        {
          label: ls('Java Yazılım Geliştirme', 'Java Development'),
          href: '/hizmetler/java-yazilim-gelistirme-hizmetleri',
        },
        {
          label: ls('.NET Yazılım Geliştirme', '.NET Development'),
          href: '/hizmetler/net-yazilim-gelistirme-hizmetleri',
        },
        {
          label: ls('Mobil Uygulama Geliştirme', 'Mobile Development'),
          href: '/hizmetler/mobil-uygulama-gelistirme-hizmetleri',
        },
      ],
    },
  ],

  contact: {
    telefon: '+90 (312) 235 1022',
    telefonHref: 'tel:+903122351022',
    email: 'info@securesys.com.tr',
    // WhatsApp numarası (uluslararası, sadece rakam). Sistemdeki telefon ile
    // aynı (+90 312 235 1022 → 903122351022). wa.me/<numara> formatında.
    whatsapp: '903122351022',
    whatsappMessage: ls(
      'Merhaba, SecureSys hizmetleri hakkında bilgi almak istiyorum.',
      'Hello, I would like to get information about SecureSys services.',
    ),
    adres: ls(
      'Çayyolu - Ümit Mahallesi, 2544 Sokak No: 3/1, Çankaya / Ankara',
      'Çayyolu - Ümit Mahallesi, 2544 Sokak No: 3/1, Çankaya / Ankara, Turkey',
    ),
    calismaSaatleri: ls('Pzt-Cum 09:00 - 18:00', 'Mon-Fri 09:00 - 18:00'),
    mapsUrl: 'https://maps.app.goo.gl/AK5etrj5NycxaZWS7',
    mapsEmbedUrl:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3061.099344898047!2d32.69153157638228!3d39.894407987263435!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14d3390f393bcdc5%3A0x3ecbee4519b47c74!2sSecuresys%20Bilgi%20Teknolojileri%20Yaz%C4%B1l%C4%B1mevi!5e0!3m2!1str!2str!4v1779981738755!5m2!1str!2str',
  },

  social: [
    {
      platform: 'linkedin',
      icon: 'Linkedin',
      href: 'https://www.linkedin.com/company/securesys-bilgi-teknolojileri',
      label: ls('LinkedIn', 'LinkedIn'),
    },
  ],

  copyright: ls(
    '© {year} Securesys Bilgi Teknolojileri Ltd. Şti. Tüm hakları saklıdır.',
    '© {year} Securesys Bilgi Teknolojileri Ltd. Şti. All rights reserved.',
  ),

  legalLinks: [
    { label: ls('KVKK Aydınlatma', 'Privacy Notice'), href: '/kvkk-aydinlatma' },
    { label: ls('Gizlilik Politikası', 'Privacy Policy'), href: '/gizlilik-politikasi' },
    { label: ls('Çerez Politikası', 'Cookie Policy'), href: '/cerez-politikasi' },
  ],

  // DeepL otomatik çeviri — admin /cms/ayarlar'dan enable edip API key girer.
  // Default: kapalı; key girilince /cms/diller/[code]/ceviriler'de buton açılır.
  deepl: {
    enabled: false,
    plan: 'free',
    apiKey: '',
  },
}
